"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Rocket,
  LayoutDashboard,
  Users,
  FileText,
  Briefcase,
  Receipt,
  CalendarDays,
  CalendarCheck,
  BarChart3,
  UserCog,
  Globe,
  Settings,
  Workflow,
  Smartphone,
  Keyboard,
  ArrowRight,
  Repeat,
  Palette,
  Zap,
} from "lucide-react";
import { HelpSearchInput } from "@/components/help/HelpSearchInput";
import { HelpSearchResults } from "@/components/help/HelpSearchResults";
import { searchHelpArticles, SearchResult } from "@/components/help/help-search-index";
import { useTranslations } from "@/hooks/useTranslations";

export default function HelpPage() {
  const t = useTranslations();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setIsSearching(true);

    if (query.trim()) {
      const results = searchHelpArticles(query);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }

    setIsSearching(false);
  };

  // Show search results when there's a query
  const showSearchResults = searchQuery.trim().length > 0;

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
          {t("help.title")}
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          {t("help.description")}
        </p>
      </div>

      {/* Search Bar */}
      <div className="max-w-2xl mx-auto">
        <HelpSearchInput onSearch={handleSearch} />
      </div>

      {/* Search Results or Default Content */}
      {showSearchResults ? (
        <div className="max-w-4xl mx-auto">
          <HelpSearchResults
            results={searchResults}
            query={searchQuery}
            isSearching={isSearching}
          />
        </div>
      ) : (
        <>
          {/* Quick Start Links */}
          <QuickStartSection />

          {/* Feature Documentation */}
          <FeatureDocumentationSection />

          {/* Guides */}
          <GuidesSection />

          {/* Workflow Diagram */}
          <WorkflowDiagram />
        </>
      )}
    </div>
  );
}

function QuickStartSection() {
  const t = useTranslations();

  const quickLinks = [
    {
      title: t("help.quickLinks.quickStart.title"),
      description: t("help.quickLinks.quickStart.description"),
      href: "/help/getting-started/quick-start",
      icon: Rocket,
      color: "bg-success/10 text-success dark:bg-green-900/30 dark:text-green-400",
    },
    {
      title: t("help.quickLinks.firstQuote.title"),
      description: t("help.quickLinks.firstQuote.description"),
      href: "/help/getting-started/first-quote",
      icon: FileText,
      color: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
    },
    {
      title: t("help.quickLinks.dashboardOverview.title"),
      description: t("help.quickLinks.dashboardOverview.description"),
      href: "/help/getting-started/dashboard-overview",
      icon: LayoutDashboard,
      color: "bg-info/10 text-info dark:bg-blue-900/30 dark:text-info",
    },
  ];

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {t("help.sections.getStarted")}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {quickLinks.map((link) => {
          const Icon = link.icon;
          return (
            <Link
              key={link.href}
              href={link.href}
              className="group flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-info dark:hover:border-blue-600 transition-colors"
            >
              <div className={`p-2 rounded-lg ${link.color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 dark:text-white group-hover:text-info dark:group-hover:text-info transition-colors">
                  {link.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {link.description}
                </p>
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-info transition-colors flex-shrink-0" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function FeatureDocumentationSection() {
  const t = useTranslations();

  const featureCategories = [
    {
      title: t("help.featureCategories.clientManagement.title"),
      items: [
        { title: t("help.featureCategories.clientManagement.clients"), href: "/help/features/clients", icon: Users },
        { title: t("help.featureCategories.clientManagement.clientPortal"), href: "/help/features/client-portal", icon: Globe },
      ],
    },
    {
      title: t("help.featureCategories.jobsQuotes.title"),
      items: [
        { title: t("help.featureCategories.jobsQuotes.quotes"), href: "/help/features/quotes", icon: FileText },
        { title: t("help.featureCategories.jobsQuotes.jobs"), href: "/help/features/jobs", icon: Briefcase },
        { title: t("help.featureCategories.jobsQuotes.invoices"), href: "/help/features/invoices", icon: Receipt },
        { title: t("help.featureCategories.jobsQuotes.recurringJobs"), href: "/help/features/recurring-jobs", icon: Repeat },
      ],
    },
    {
      title: t("help.featureCategories.scheduling.title"),
      items: [
        { title: t("help.featureCategories.scheduling.schedule"), href: "/help/features/schedule", icon: CalendarDays },
        { title: t("help.featureCategories.scheduling.onlineBookings"), href: "/help/features/bookings", icon: CalendarCheck },
      ],
    },
    {
      title: t("help.featureCategories.businessTools.title"),
      items: [
        { title: t("help.featureCategories.businessTools.reports"), href: "/help/features/reports", icon: BarChart3 },
        { title: t("help.featureCategories.businessTools.team"), href: "/help/features/team", icon: UserCog },
        { title: t("help.featureCategories.businessTools.branding"), href: "/help/features/branding", icon: Palette },
        { title: t("help.featureCategories.businessTools.automations"), href: "/help/features/automations", icon: Zap },
        { title: t("help.featureCategories.businessTools.settings"), href: "/help/features/settings", icon: Settings },
      ],
    },
  ];

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {t("help.sections.features")}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {featureCategories.map((category) => (
          <div
            key={category.title}
            className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
          >
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">
              {category.title}
            </h3>
            <ul className="space-y-2">
              {category.items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-info dark:hover:text-info transition-colors"
                    >
                      <Icon className="h-4 w-4 text-gray-400" />
                      <span>{item.title}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function GuidesSection() {
  const t = useTranslations();

  const guides = [
    {
      title: t("help.guides.mobileTips.title"),
      description: t("help.guides.mobileTips.description"),
      href: "/help/guides/mobile-tips",
      icon: Smartphone,
    },
    {
      title: t("help.guides.keyboardShortcuts.title"),
      description: t("help.guides.keyboardShortcuts.description"),
      href: "/help/guides/keyboard-shortcuts",
      icon: Keyboard,
    },
  ];

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {t("help.sections.guides")}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {guides.map((guide) => {
          const Icon = guide.icon;
          return (
            <Link
              key={guide.href}
              href={guide.href}
              className="group flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-info dark:hover:border-blue-600 transition-colors"
            >
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 dark:text-white group-hover:text-info dark:group-hover:text-info transition-colors">
                  {guide.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {guide.description}
                </p>
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-info transition-colors flex-shrink-0" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function WorkflowDiagram() {
  const t = useTranslations();

  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-6 border border-info dark:border-blue-800">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {t("help.workflow.title")}
      </h2>
      <div className="flex flex-wrap items-center justify-center gap-2 text-sm">
        <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg shadow-sm">
          <Users className="h-4 w-4 text-info" />
          <span className="font-medium">{t("help.workflow.client")}</span>
        </div>
        <ArrowRight className="h-4 w-4 text-gray-400" />
        <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg shadow-sm">
          <FileText className="h-4 w-4 text-success" />
          <span className="font-medium">{t("help.workflow.quote")}</span>
        </div>
        <ArrowRight className="h-4 w-4 text-gray-400" />
        <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg shadow-sm">
          <Briefcase className="h-4 w-4 text-purple-500" />
          <span className="font-medium">{t("help.workflow.job")}</span>
        </div>
        <ArrowRight className="h-4 w-4 text-gray-400" />
        <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg shadow-sm">
          <CalendarDays className="h-4 w-4 text-orange-500" />
          <span className="font-medium">{t("help.workflow.schedule")}</span>
        </div>
        <ArrowRight className="h-4 w-4 text-gray-400" />
        <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg shadow-sm">
          <Receipt className="h-4 w-4 text-emerald-500" />
          <span className="font-medium">{t("help.workflow.invoice")}</span>
        </div>
      </div>
      <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-4">
        {t("help.workflow.learnMore")}{" "}
        <Link href="/help/guides/complete-workflow" className="text-info dark:text-info hover:underline font-medium">
          {t("help.workflow.completeGuide")}
        </Link>
      </p>
    </div>
  );
}
